/*
* Broadcast Theme
*
* Use this file to add custom Javascript to Broadcast.  Keeping your custom
* Javascript in this fill will make it easier to update Broadcast. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/


(function() {
  // Add custom code below this line

  /**
   * GeminiSlider - Lightweight, Robust Slider Controller
   * Handles both static (Collection) and async (Recently Viewed) content.
   * Uses Observers to detect visibility and content injection.
   */
  class GeminiSlider {
    constructor(container) {
      this.container = container;
      this.prevBtn = container.querySelector('[data-gemini-prev]');
      this.nextBtn = container.querySelector('[data-gemini-next]');
      this.scroller = container.querySelector('.grid-outer-gemini');

      this.initialized = false;
      this.resizeObserver = null;
      this.mutationObserver = null;
      this.containerObserver = null; // For watching async injection
      this.observerTimeout = null;
      this.checkRaf = null;
      this.scrollRaf = null;
      this.resizeRaf = null;
      this._lastArrowTop = null;

      this.container.geminiSlider = this; // Store reference

      // If missing buttons, abort (buttons are usually static)
      if (!this.prevBtn || !this.nextBtn) return;

      this.handleScroll = this.handleScroll.bind(this);
      this.handlePrev = this.handlePrev.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.checkAndInit = this.checkAndInit.bind(this);
      this.scheduleCheckAndInit = this.scheduleCheckAndInit.bind(this);
      this.handleContainerMutation = this.handleContainerMutation.bind(this);
      this.handleResize = this.handleResize.bind(this);

      if (this.scroller) {
          this.init();
      } else {
          // Scroller missing? Wait for AJAX injection
          this.waitForScroller();
      }
    }

    waitForScroller() {
        this.containerObserver = new MutationObserver(this.handleContainerMutation);
        this.containerObserver.observe(this.container, { childList: true, subtree: true });
        
        // Safety timeout: Stop watching if content doesn't load within 10 seconds
        this.observerTimeout = setTimeout(() => {
            if (this.containerObserver) {
                console.warn('GeminiSlider: Scroller not found within timeout. Aborting observation.', this.container);
                this.containerObserver.disconnect();
                this.containerObserver = null;
            }
        }, 10000);
    }

    handleContainerMutation(mutations) {
        const scroller = this.container.querySelector('.grid-outer-gemini');
        if (scroller) {
            this.scroller = scroller;
            // Stop watching container once found
            if (this.containerObserver) {
                this.containerObserver.disconnect();
                this.containerObserver = null;
            }
            if (this.observerTimeout) {
                clearTimeout(this.observerTimeout);
                this.observerTimeout = null;
            }
            this.init();
        }
    }

    init() {
      // 1. Try to initialize immediately (for static sections like Collection)
      this.checkAndInit();

      // 2. Setup Observers for Async/Dynamic content (Recently Viewed / Related)
      this.setupObservers();

      // 3. Handle Window Resize (Global)
      window.addEventListener('resize', this.handleResize);
    }

    setupObservers() {
        if (!this.scroller) return;

        // ResizeObserver: Detects when container becomes visible (width > 0)
        // Critical for sections inside hidden tabs or initially hidden wrappers
        this.resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                if (entry.contentRect.width > 0) {
                     this.scheduleCheckAndInit();
                }
            }
        });
        this.resizeObserver.observe(this.scroller);

        // MutationObserver: Detects when products are added via AJAX inside the scroller
        // Critical for Recently Viewed / Related Products if they append items
        this.mutationObserver = new MutationObserver(mutations => {
            this.scheduleCheckAndInit();
        });
        this.mutationObserver.observe(this.scroller, { childList: true, subtree: true });
    }

    scheduleCheckAndInit() {
        if (this.checkRaf) return;
        this.checkRaf = window.requestAnimationFrame(() => {
            this.checkRaf = null;
            this.checkAndInit();
        });
    }

    handleResize() {
        if (!this.initialized) return;
        if (this.resizeRaf) return;
        this.resizeRaf = window.requestAnimationFrame(() => {
            this.resizeRaf = null;
            this.updateUI();
        });
    }

    checkAndInit() {
        if (this.initialized) {
            // Even if initialized, check if we need to update arrows (e.g. products added)
            this.updateArrows();
            return;
        }

        if (!this.scroller) return;

        // Criteria: Scroller has width AND has product items
        const hasWidth = this.scroller.clientWidth > 0;
        const items = this.scroller.querySelectorAll('.grid-item-gemini');
        const hasItems = items.length > 0;

        if (hasWidth && hasItems) {
            this.setupController();
        }
    }

    setupController() {
        this.initialized = true;

        // Bind Events
        this.scroller.addEventListener('scroll', this.handleScroll, { passive: true });
        this.prevBtn.addEventListener('click', this.handlePrev);
        this.nextBtn.addEventListener('click', this.handleNext);

        // Initial State
        // Use RAF to ensure DOM layout is settled (images, etc.)
        window.requestAnimationFrame(() => {
            this.updateUI();
        });
    }

    updateArrowPosition() {
        // Kept for compatibility: measure (read) then apply (write).
        this.applyArrowPosition(this.measureArrowPosition());
    }

    measureArrowPosition() {
        // READ-ONLY: returns the vertical center of the first product image, or null.
        // We target the CONTAINER (.product-item__bg), which respects the aspect ratio rule.
        if (!this.container) return null;
        const productItemBg = this.container.querySelector('.product-item__bg');
        if (!productItemBg) return null;

        // Measure the rendered rectangles relative to the viewport
        const itemRect = productItemBg.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();

        // Distance from the top of the main container to the top of the image
        // (accounts for any padding, margins, or headings above the grid), plus
        // half the rendered image height to reach its center.
        const offsetTop = itemRect.top - containerRect.top;
        return offsetTop + (itemRect.height / 2);
    }

    applyArrowPosition(centerPosition) {
        // WRITE-ONLY: skip redundant writes to avoid needless style invalidation.
        if (centerPosition === null || centerPosition === this._lastArrowTop) return;
        this._lastArrowTop = centerPosition;
        this.container.style.setProperty('--arrow-top', `${centerPosition}px`);
    }

    updateUI() {
        // Batched read-then-write pass to avoid forced reflow (layout thrashing).
        if (!this.scroller) return;

        // READ PHASE: gather all geometry before touching the DOM.
        const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
        const centerPosition = this.measureArrowPosition();

        // WRITE PHASE: apply all style/attribute changes together.
        this.applyArrowState(scrollLeft, scrollWidth, clientWidth);
        this.applyArrowPosition(centerPosition);
    }

    destroy() {
      if (this.scroller) {
        this.scroller.removeEventListener('scroll', this.handleScroll);
      }
      window.removeEventListener('resize', this.handleResize);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.handlePrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.handleNext);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
      if (this.containerObserver) this.containerObserver.disconnect();
      if (this.observerTimeout) clearTimeout(this.observerTimeout);
      if (this.checkRaf) window.cancelAnimationFrame(this.checkRaf);
      if (this.scrollRaf) window.cancelAnimationFrame(this.scrollRaf);
      if (this.resizeRaf) window.cancelAnimationFrame(this.resizeRaf);
    }

    handleScroll() {
      if (this.scrollRaf) return;
      this.scrollRaf = window.requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.updateArrows();
      });
    }

    updateArrows() {
      if (!this.scroller) return;
      // READ then WRITE in one place; safe when called on its own (e.g. scroll).
      const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
      this.applyArrowState(scrollLeft, scrollWidth, clientWidth);
    }

    applyArrowState(scrollLeft, scrollWidth, clientWidth) {
      // WRITE-ONLY: operates purely on values measured during the read phase.
      // Use a small tolerance for "end" detection
      const isStart = scrollLeft <= 2;
      const isEnd = scrollLeft + clientWidth >= scrollWidth - 2;
      const hasOverflow = scrollWidth > clientWidth + 2;

      // Prev Button Logic
      if (isStart) {
        this.prevBtn.setAttribute('disabled', 'true');
        this.prevBtn.style.opacity = '0';
        this.prevBtn.style.visibility = 'hidden';
      } else {
        this.prevBtn.removeAttribute('disabled');
        this.prevBtn.style.opacity = '';
        this.prevBtn.style.visibility = 'visible';
      }

      // Next Button Logic
      if (!hasOverflow || isEnd) {
        this.nextBtn.setAttribute('disabled', 'true');
        this.nextBtn.style.opacity = '0';
        this.nextBtn.style.visibility = 'hidden';
      } else {
        this.nextBtn.removeAttribute('disabled');
        this.nextBtn.style.opacity = '';
        this.nextBtn.style.visibility = 'visible';
      }
    }

    handlePrev(e) {
      e.preventDefault();
      this.scrollBy(-1);
    }

    handleNext(e) {
      e.preventDefault();
      this.scrollBy(1);
    }

    scrollBy(direction) {
      if (!this.scroller) return;
      
      // Calculate scroll amount: roughly one "page" or item width
      const scrollAmount = this.scroller.clientWidth * 0.8;
      
      this.scroller.scrollBy({
        left: scrollAmount * direction,
        behavior: 'smooth'
      });
    }
  }

  // Initialize sliders on page load and section load
  function initGeminiSliders() {
    const containers = document.querySelectorAll('[data-gemini-slider-container]');
    containers.forEach(container => {
      // Check if already initialized to prevent duplicates
      if (container.dataset.geminiInitialized) return;
      
      new GeminiSlider(container);
      container.dataset.geminiInitialized = 'true';
    });
  }

  // Run on Load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGeminiSliders);
  } else {
    initGeminiSliders();
  }

  // Run on Shopify Section Load (Editor)
  document.addEventListener('shopify:section:load', initGeminiSliders);

})();


/**
 * Gemini2: Persistent mega menu backdrop (Linjer / Missoma style)
 *
 * - meganav--visible while cursor is in .header__menu (no wrapper toggle between items).
 * - showDisclosure inlined: never removes meganav--visible (avoids origShow remove/re-add paint gap).
 *
 * Transition speed: data-menu-transition on header wrapper (schema). Hide delay = 25% of that.
 */
(function() {
  var HD = customElements.get('hover-disclosure');
  if (!HD) return;

  var proto = HD.prototype;

  function getTiming() {
    var wrap = document.querySelector('[data-header-wrapper]');
    var raw = wrap ? parseInt(wrap.getAttribute('data-menu-transition'), 10) : NaN;
    var transitionSpeed = Number.isFinite(raw) && raw >= 0 ? raw : 300;
    return {
      transitionSpeed: transitionSpeed,
      hideDelay: Math.round(transitionSpeed * 0.25),
    };
  }

  function cleanSibling(sib) {
    sib.classList.remove('is-visible');
    if (sib.disclosure) sib.disclosure.classList.remove('is-visible');
    sib.setAttribute('aria-expanded', false);
  }

  function anyDisclosureOpen(wrapper) {
    if (!wrapper) return false;
    return Array.prototype.some.call(wrapper.querySelectorAll('hover-disclosure'), function(el) {
      return el.classList.contains('is-visible');
    });
  }

  function bindMenuBackdrop() {
    var menu = document.querySelector('.header__menu');
    var wrap = document.querySelector('[data-header-wrapper]');
    if (!menu || !wrap || menu.dataset.geminiMenuBackdropBound === 'true') return;
    menu.dataset.geminiMenuBackdropBound = 'true';

    menu.addEventListener('mouseenter', function() {
      if (menu._geminiMenuLeaveTimeout) {
        clearTimeout(menu._geminiMenuLeaveTimeout);
        menu._geminiMenuLeaveTimeout = null;
      }
      wrap.classList.add('meganav--visible');
    });

    menu.addEventListener('mouseleave', function() {
      var hideDelay = getTiming().hideDelay;
      if (menu._geminiMenuLeaveTimeout) clearTimeout(menu._geminiMenuLeaveTimeout);
      menu._geminiMenuLeaveTimeout = setTimeout(function() {
        menu._geminiMenuLeaveTimeout = null;
        if (!anyDisclosureOpen(wrap)) {
          wrap.classList.remove('meganav--visible', 'meganav--is-transitioning');
        }
      }, hideDelay);
    });
  }

  bindMenuBackdrop();
  document.addEventListener('shopify:section:load', function() {
    var menu = document.querySelector('.header__menu');
    if (menu) delete menu.dataset.geminiMenuBackdropBound;
    bindMenuBackdrop();
  });

  proto.showDisclosure = function(e) {
    var timing = getTiming();
    var transitionSpeed = timing.transitionSpeed;

    if (this._hideTimeout) {
      clearTimeout(this._hideTimeout);
      this._hideTimeout = null;
    }

    if (this.wrapper) {
      var self = this;
      this.wrapper.querySelectorAll('hover-disclosure').forEach(function(sib) {
        if (sib === self) return;
        if (sib._hideTimeout) {
          clearTimeout(sib._hideTimeout);
          sib._hideTimeout = null;
        }
        if (sib.classList.contains('is-visible')) {
          cleanSibling(sib);
        }
      });
    }

    if (e && e.type === 'mouseenter' && this.wrapper) {
      this.wrapper.classList.add('meganav--is-transitioning');
    }
    if (this.wrapper) {
      this.wrapper.classList.add('meganav--visible');
    }
    this.setAttribute('aria-expanded', true);
    this.classList.add('is-visible');
    if (this.disclosure) this.disclosure.classList.add('is-visible');

    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout);
    }
    var w = this.wrapper;
    var inst = this;
    this.transitionTimeout = setTimeout(function() {
      if (w) w.classList.remove('meganav--is-transitioning');
      inst.transitionTimeout = 0;
    }, transitionSpeed);
  };

  proto.hideDisclosure = function() {
    var self = this;
    var hideDelay = getTiming().hideDelay;
    this._hideTimeout = setTimeout(function() {
      self._hideTimeout = null;
      cleanSibling(self);

      var wrap = self.wrapper;
      if (!wrap) return;

      if (!anyDisclosureOpen(wrap)) {
        wrap.classList.remove('meganav--visible', 'meganav--is-transitioning');
      }
    }, hideDelay);
  };
})();
